import React, { useEffect, useState } from 'react';
import { FaFileDownload, FaRegFileAlt } from 'react-icons/fa';
import PageMetaLoader from '../components/PageMetaLoader';
import { useI18n } from '../i18n';
import { en, km } from './DownloadsPage.data';

interface DownloadItem {
  fileName: string;
  displayName?: string;
  description?: string;
}

const DownloadsPage: React.FC = () => {
  const { locale } = useI18n();
  const t = locale === 'km' ? km : en;
  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDownloads = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await fetch('/files/downloads.json', { cache: 'no-cache' });

        if (!response.ok) {
          throw new Error('Failed to load downloads');
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error('Invalid downloads data format');
        }

        const normalizedDownloads = data
          .filter((item) => item && item.fileName)
          .map((item: DownloadItem) => ({
            fileName: item.fileName,
            displayName: item.displayName || item.fileName,
            description: item.description,
          }));

        setDownloads(normalizedDownloads);
        setError(null);
      } catch (err) {
        console.error('Error loading downloads:', err);
        setError(t.errorFetch);
      } finally {
        setLoading(false);
      }
    };

    fetchDownloads();
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-center">
          {error}
        </div>
      );
    }

    if (downloads.length === 0) {
      return (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-8 text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{t.emptyTitle}</h3>
          <p className="text-gray-600">
            {t.emptyBodyPrefix}
            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">public/downloads</code>
            {t.emptyBodyMiddle}
            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">downloads.json</code>
            {t.emptyBodySuffix}
          </p>
        </div>
      );
    }

    return (
      <ul className="space-y-4">
        {downloads.map((item) => (
          <li key={item.fileName} className="bg-white shadow-sm rounded-lg border border-gray-100 p-4">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-blue-50 text-primary flex items-center justify-center">
                <FaRegFileAlt size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{item.displayName || item.fileName}</h3>
                <p className="text-sm text-gray-600">{item.description || item.fileName}</p>
              </div>
              <a
                href={`/files/${encodeURIComponent(item.fileName)}`}
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <FaFileDownload aria-hidden />
                <span>{t.download}</span>
              </a>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <PageMetaLoader pageUrl="downloads" />
      <section className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-primary font-semibold uppercase tracking-wide mb-2">{t.eyebrow}</p>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">{t.title}</h1>
            <p className="text-lg text-gray-600">
              {t.subtitle}
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-blue-50 text-primary">
                  <FaFileDownload />
                </span>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{t.availableTitle}</h2>
                  <p className="text-sm text-gray-600">
                    {t.availableDesc}
                  </p>
                </div>
              </div>
              {renderContent()}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default DownloadsPage;

